import { useEffect, useRef, useState } from "react";
import api, { errMsg, fileUrl, INTEREST_TAGS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { User as UserIcon, Users, Heart, Plus, X, Mail, ShieldCheck, Upload, KeyRound, Trash2, Bell, UserPlus, Link as LinkIcon, Unlink, Copy, ShoppingBag, ChevronDown, Check } from "lucide-react";

const PRODUCT_CATEGORY_OPTIONS = [
  "Curriculum and Learning Materials",
  "Educational Toys and Games",
  "Books Media and Printables",
  "Kids Clothing and Shoes",
  "Sports PE and Outdoor Gear",
  "Arts Crafts and Music",
  "Desks and Learning Space",
  "Tech Electronics and Lab Gear",
  "Baby and Toddler Essentials",
  "Handcrafted and Local Goods",
  "Free and Community Swap",
  "Other",
];

export default function Profile() {
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [firstName, setFirstName] = useState("");
  const [surname, setSurname] = useState("");
  const photoInput = useRef(null);
  const [children, setChildren] = useState([]);
  const [interests, setInterests] = useState([]);
  const [customTag, setCustomTag] = useState("");
  const [about, setAbout] = useState("");
  const [saving, setSaving] = useState(false);
  const [resettingPw, setResettingPw] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  // Email mirror preference. Defaults to true — users are opted in to receiving
  // email copies of every in-app notification unless they explicitly turn it off.
  const [notificationEmails, setNotificationEmails] = useState(false);
  // Second-parent invite surface (parent role only). `partner` is the linked
  // user (if any) and `unusedToken` is the share token for the open invite.
  const [partner, setPartner] = useState(null);
  const [unusedToken, setUnusedToken] = useState("");
  const [inviteBusy, setInviteBusy] = useState(false);

  const loadInviteStatus = async () => {
    if (user?.role !== "parent") return;
    try {
      const { data } = await api.get("/parent-invite/status");
      setPartner(data.partner || null);
      setUnusedToken(data.unused_token || "");
    } catch { /* silent — no partner means no block */ }
  };

  const inviteLink = unusedToken ? `${window.location.origin}/invite/${unusedToken}` : "";

  const generateInvite = async () => {
    setInviteBusy(true);
    try {
      const { data } = await api.post("/parent-invite/create");
      setUnusedToken(data.token);
      toast.success("Invite link ready — copy & share it with your partner.");
    } catch (e) {
      toast.error(errMsg(e, "Could not create invite"));
    } finally { setInviteBusy(false); }
  };
  const revokeInvite = async () => {
    setInviteBusy(true);
    try {
      await api.post("/parent-invite/revoke");
      setUnusedToken("");
      toast.success("Invite link revoked.");
    } catch (e) { toast.error(errMsg(e, "Could not revoke invite")); }
    finally { setInviteBusy(false); }
  };
  const copyInviteLink = async () => {
    try { await navigator.clipboard.writeText(inviteLink); toast.success("Link copied to clipboard."); }
    catch { toast.error("Couldn't copy — long-press the link to copy manually."); }
  };
  const unlinkPartner = async () => {
    if (!window.confirm(`Unlink ${partner?.name || partner?.email} from your profile? They'll lose shared access immediately.`)) return;
    setInviteBusy(true);
    try {
      await api.post("/parent-invite/unlink");
      setPartner(null);
      toast.success("Partner unlinked.");
    } catch (e) { toast.error(errMsg(e, "Could not unlink partner")); }
    finally { setInviteBusy(false); }
  };

  const requestPasswordReset = async () => {
    if (!user?.email) return;
    setResettingPw(true);
    try {
      await api.post("/auth/forgot-password", { email: user.email, role: user.role });
      toast.success(`Reset link sent to ${user.email}. Check your inbox.`);
    } catch (e) {
      toast.error(errMsg(e, "Could not send reset link"));
    } finally {
      setResettingPw(false);
    }
  };

  const confirmDelete = async (reason) => {
    setDeleting(true);
    try {
      await api.delete("/users/me", { params: reason ? { reason } : {} });
      toast.success("Your account has been permanently deleted.");
      if (logout) await logout();
      navigate("/", { replace: true });
    } catch (e) {
      toast.error(errMsg(e, "Could not delete account"));
      setDeleting(false);
    }
  };

  useEffect(() => {
    if (user) {
      setName(user.name || "");
      setFirstName(user.first_name || (user.name || "").split(" ")[0] || "");
      setSurname(user.surname || "");
      setChildren(user.children || []);
      setInterests(user.interests || []);
      setAbout(user.about_family || "");
      setNotificationEmails(user.notification_emails === true);
      loadInviteStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (!user) return null;
  const isParent = user.role === "parent";
  const isMarketplace = user.role === "marketplace_user";

  const syncCount = (n) => {
    const c = Math.max(0, Math.min(12, n || 0));
    setChildren((prev) => Array.from({ length: c }, (_, i) => prev[i] || { age: "", gender: "" }));
  };

  const setChild = (i, key, val) => {
    const arr = [...children];
    arr[i] = { ...arr[i], [key]: val };
    setChildren(arr);
  };

  const toggleTag = (t) =>
    setInterests((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));

  const addCustom = () => {
    const t = customTag.trim();
    if (t && !interests.map((x) => x.toLowerCase()).includes(t.toLowerCase())) {
      setInterests([...interests, t]);
    }
    setCustomTag("");
  };

  const save = async () => {
    if (!firstName.trim()) {
      toast.error("First name is required");
      return;
    }
    setSaving(true);
    try {
      await api.put("/profile", {
        name,
        first_name: firstName,
        surname,
        children: children.filter((c) => String(c.age).trim()),
        interests,
        about_family: about,
        notification_emails: notificationEmails,
      });
      await refresh();
      toast.success("Profile updated!");
    } catch (e) {
      toast.error(errMsg(e, "Could not save profile"));
    } finally {
      setSaving(false);
    }
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/profile/photo", fd);
      await refresh();
      toast.success("Photo submitted for admin approval");
    } catch (e) {
      toast.error(errMsg(e, "Upload failed"));
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="profile-page">
      <div className="mb-8 fade-in">
        <p className="text-sm text-stone-600 font-semibold tracking-wide uppercase">My account</p>
        <h1 className="text-3xl sm:text-4xl font-black text-forest mt-1">Profile</h1>
      </div>

      <div className="bg-white/80 border border-sage/20 rounded-3xl p-6 sm:p-8 shadow-sm space-y-8" data-testid="profile-form">
        <div className="flex flex-wrap items-center gap-4 pb-6 border-b border-sage/15">
          {user.photo_status === "approved" && user.photo_path ? (
            <img src={fileUrl(user.photo_path)} alt="Profile" className="w-14 h-14 rounded-full object-cover border border-sage/30" data-testid="profile-photo" />
          ) : (
            <img src="/logo.png" alt="Compass Kids logo" className="w-14 h-14 rounded-full object-cover border border-sage/30 bg-white" data-testid="profile-photo-placeholder" />
          )}
          <div>
            <p className="font-heading font-bold text-forest text-lg">{user.name}</p>
            <p className="text-sm text-forest/60 flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> {user.email}</p>
            <div className="flex gap-2 mt-1">
              <span className="text-[10px] font-bold uppercase tracking-wide bg-sage-light text-sage-dark px-2.5 py-0.5 rounded-full">{user.role}</span>
              <span className={`text-[10px] font-bold uppercase tracking-wide px-2.5 py-0.5 rounded-full ${user.subscription_status === "active" || user.role === "admin" ? "bg-sage-light text-sage-dark" : "bg-amber-100 text-amber-800"}`} data-testid="profile-subscription-status">
                {user.role === "admin" ? "Admin" : user.role === "parent" ? "Free account" : user.subscription_status === "active" ? "Subscribed" : "Not subscribed"}
              </span>
            </div>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-forest/80 flex items-center gap-2 mb-2"><UserIcon className="w-4 h-4 text-sage-dark" /> First name *</Label>
            <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} maxLength={40}
              className="rounded-xl bg-softstone/60 border-sage/30" data-testid="profile-first-name-input" />
          </div>
          <div>
            <Label className="text-forest/80 mb-2 block">Surname</Label>
            <Input value={surname} onChange={(e) => setSurname(e.target.value)} maxLength={40}
              className="rounded-xl bg-softstone/60 border-sage/30" data-testid="profile-surname-input" />
          </div>
        </div>
        <div>
          <Label className="text-forest/80 mb-2 block">Profile photo</Label>
          <input ref={photoInput} type="file" accept="image/*" className="hidden" onChange={(e) => uploadPhoto(e.target.files[0])} data-testid="profile-photo-input" />
          <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={() => photoInput.current?.click()} data-testid="profile-photo-btn">
            <Upload className="w-4 h-4 mr-1" /> {user.photo_status === "pending" ? "Replace pending photo" : user.photo_status === "rejected" ? "Try a new photo" : (user.photo_status === "approved" && user.photo_path) ? "Change photo" : "Upload photo (optional)"}
          </Button>
          {user.photo_status === "pending" && (
            <p className="text-xs text-forest/60 mt-2" data-testid="profile-photo-pending">Your photo is pending admin approval — the Compass Kids logo stays visible until it is approved.</p>
          )}
          {user.photo_status === "rejected" && (
            <p className="text-xs text-terracotta-dark mt-2" data-testid="profile-photo-rejected">
              Your last photo was not approved{user.photo_rejection_reason ? ` (${user.photo_rejection_reason})` : ""}. Please upload a new one.
            </p>
          )}
        </div>

        {isParent && (
          <>
            <div>
              <Label className="text-forest/80 flex items-center gap-2 mb-3"><Users className="w-4 h-4 text-sage-dark" /> Children</Label>
              <div className="flex items-center gap-3 mb-3">
                <span className="text-sm text-forest/60">Number of children:</span>
                <Input type="number" min={0} max={12} value={children.length}
                  onChange={(e) => syncCount(parseInt(e.target.value))}
                  className="w-24 rounded-xl bg-softstone/60 border-sage/30" data-testid="profile-children-count" />
              </div>
              <div className="space-y-2" data-testid="profile-children-rows">
                {children.length > 0 && (
                  <div className="flex gap-2 items-center text-xs font-bold uppercase tracking-wide text-forest/50 px-1" data-testid="profile-children-headers">
                    <span className="flex-1" data-testid="profile-children-header-age">Age</span>
                    <span className="flex-1" data-testid="profile-children-header-gender">Gender</span>
                  </div>
                )}
                {children.map((c, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <Input value={c.age} onChange={(e) => setChild(i, "age", e.target.value)}
                      placeholder={`Child ${i + 1} age`} className="rounded-xl bg-softstone/60 border-sage/30" data-testid={`profile-child-age-${i}`} />
                    <Select value={c.gender} onValueChange={(v) => setChild(i, "gender", v)}>
                      <SelectTrigger className="rounded-xl bg-softstone/60 border-sage/30" data-testid={`profile-child-gender-${i}`}>
                        <SelectValue placeholder="Gender" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Male">Male</SelectItem>
                        <SelectItem value="Female">Female</SelectItem>
                        <SelectItem value="Prefer not to say">Prefer not to say</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-forest/80 flex items-center gap-2 mb-3"><Heart className="w-4 h-4 text-terracotta-dark" /> Family interests</Label>
              <div className="flex flex-wrap gap-2 mb-3" data-testid="profile-interest-presets">
                {INTEREST_TAGS.map((t) => (
                  <button key={t} type="button" onClick={() => toggleTag(t)}
                    className={`text-xs font-bold px-3.5 py-2 rounded-full border transition-colors ${interests.includes(t) ? "bg-sage text-white border-sage" : "bg-white text-forest/70 border-sage/30 hover:border-sage"}`}
                    data-testid={`profile-interest-${t.toLowerCase()}`}>
                    {t}
                  </button>
                ))}
              </div>
              {interests.filter((t) => !INTEREST_TAGS.includes(t)).length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3" data-testid="profile-custom-tags">
                  {interests.filter((t) => !INTEREST_TAGS.includes(t)).map((t) => (
                    <span key={t} className="inline-flex items-center gap-1 text-xs font-bold px-3 py-2 rounded-full bg-terracotta-light text-terracotta-dark">
                      {t}
                      <button type="button" onClick={() => toggleTag(t)} aria-label={`Remove ${t}`}><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                </div>
              )}
              <div className="flex gap-2 max-w-md">
                <Input value={customTag} onChange={(e) => setCustomTag(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCustom())}
                  placeholder="Add your own (e.g. birdwatching)" maxLength={30}
                  className="rounded-full bg-softstone/60 border-sage/30" data-testid="profile-custom-interest-input" />
                <Button type="button" variant="outline" size="sm" onClick={addCustom} disabled={!customTag.trim()}
                  className="rounded-full shrink-0" data-testid="profile-add-custom-btn">
                  <Plus className="w-4 h-4 mr-1" /> Add
                </Button>
              </div>
            </div>

            <div>
              <Label className="text-forest/80">About our family</Label>
              <Textarea value={about} onChange={(e) => setAbout(e.target.value)} rows={3} maxLength={500}
                placeholder="We're a nature-loving family..."
                className="mt-2 rounded-xl bg-softstone/60 border-sage/30" data-testid="profile-about-input" />
            </div>
          </>
        )}

        {!isParent && !isMarketplace && (
          <p className="text-sm text-forest/60 bg-softstone/60 rounded-2xl p-4 flex items-center gap-2" data-testid="non-parent-note">
            <ShieldCheck className="w-4 h-4 text-sage-dark shrink-0" />
            {user.role === "vendor"
              ? "Your business details live in the Vendor Portal — this page covers your account name."
              : "Admin accounts manage the platform from the Admin console."}
          </p>
        )}

        {isMarketplace && <MarketplaceBusinessBlock />}

        <div className="rounded-2xl border border-sage/20 bg-softstone/40 p-4 sm:p-5 space-y-3" data-testid="email-preferences-section">
          <div className="flex items-center gap-2">
            <Bell className="w-4 h-4 text-sage-dark" />
            <h3 className="font-heading font-bold text-forest text-sm uppercase tracking-wide">Email preferences</h3>
          </div>
          <label className="flex items-start gap-3 cursor-pointer" data-testid="notification-emails-label">
            <input
              type="checkbox"
              checked={notificationEmails}
              onChange={(e) => setNotificationEmails(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-sage-dark cursor-pointer shrink-0"
              data-testid="notification-emails-toggle"
            />
            <span className="text-sm text-forest/80">
              <span className="font-bold text-forest">Email me a copy of every in-app notification</span>
              <span className="block text-xs text-forest/60 mt-0.5">
                When off, you'll still see notifications in the bell menu — we just won't mirror them to <strong>{user.email}</strong>. Account emails (password resets, admin approvals you must action, payment confirmations) are always sent.
              </span>
            </span>
          </label>
        </div>

        <Button onClick={save} disabled={saving}
          className="rounded-full bg-terracotta hover:bg-terracotta-dark text-white shadow-md px-8" data-testid="profile-save-btn">
          {saving ? "Saving..." : "Save profile"}
        </Button>

        {isParent && (
          <div className="rounded-2xl border-2 border-sage/30 bg-sage-light/30 p-4 sm:p-5" data-testid="second-parent-block">
            <div className="flex items-start gap-2 mb-3">
              <UserPlus className="w-5 h-5 text-sage-dark mt-0.5" />
              <div>
                <p className="font-heading font-bold text-forest">Second parent</p>
                <p className="text-xs text-forest/60">Link another parent to your family profile. They sign in with their own email + password and share your subscription — no extra payment.</p>
              </div>
            </div>
            {partner ? (
              <div className="rounded-xl bg-white border border-sage/30 p-3 flex items-center justify-between gap-3" data-testid="partner-linked-block">
                <div className="flex items-center gap-2 min-w-0">
                  <LinkIcon className="w-4 h-4 text-sage-dark shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-forest truncate" data-testid="partner-name">{partner.name || partner.email}</p>
                    <p className="text-xs text-forest/60 truncate" data-testid="partner-email">{partner.email}</p>
                  </div>
                </div>
                {user?.partner_role !== "secondary" && (
                  <Button size="sm" variant="outline" onClick={unlinkPartner} disabled={inviteBusy}
                    className="rounded-full border-terracotta/40 text-terracotta-dark hover:bg-terracotta-light shrink-0" data-testid="unlink-partner-btn">
                    <Unlink className="w-4 h-4 mr-1" /> Unlink user
                  </Button>
                )}
              </div>
            ) : unusedToken ? (
              <div className="space-y-2" data-testid="invite-pending-block">
                <p className="text-xs text-forest/70">Share this one-time link with your partner. It stops working the moment they sign up.</p>
                <div className="flex items-center gap-2">
                  <Input readOnly value={inviteLink} className="rounded-full bg-white border-sage/30 text-xs" data-testid="invite-link-input" onClick={(e) => e.target.select()} />
                  <Button size="sm" onClick={copyInviteLink} className="rounded-full bg-sage hover:bg-sage-dark text-white shrink-0" data-testid="copy-invite-link-btn">
                    <Copy className="w-4 h-4 mr-1" /> Copy
                  </Button>
                </div>
                <button type="button" onClick={revokeInvite} disabled={inviteBusy}
                  className="text-xs text-forest/50 hover:text-terracotta-dark underline underline-offset-2" data-testid="revoke-invite-btn">
                  Revoke this link
                </button>
              </div>
            ) : (
              <Button size="sm" onClick={generateInvite} disabled={inviteBusy}
                className="rounded-full bg-terracotta hover:bg-terracotta-dark text-white" data-testid="invite-second-parent-btn">
                <UserPlus className="w-4 h-4 mr-1" /> Invite Second Parent
              </Button>
            )}
          </div>
        )}

        <div className="pt-6 mt-2 border-t border-sage/15 flex flex-wrap gap-3" data-testid="profile-account-actions">          <Button type="button" variant="outline" onClick={requestPasswordReset} disabled={resettingPw}
            className="rounded-full border-sage/40 text-forest hover:bg-sage-light" data-testid="profile-reset-password-btn">
            <KeyRound className="w-4 h-4 mr-2" />
            {resettingPw ? "Sending..." : "Reset password"}
          </Button>
          <Button type="button" variant="outline" onClick={() => setDeleteOpen(true)} disabled={deleting}
            className="rounded-full border-terracotta/40 text-terracotta-dark hover:bg-terracotta-light" data-testid="profile-delete-account-btn">
            <Trash2 className="w-4 h-4 mr-2" />
            {deleting ? "Deleting..." : "Delete account"}
          </Button>
        </div>
        <DeleteAccountDialog open={deleteOpen} onOpenChange={setDeleteOpen} deleting={deleting} onConfirm={confirmDelete} />
      </div>
    </div>
  );
}

const DELETE_REASONS = [
  "Too expensive",
  "Not using it enough",
  "Didn't find what I needed",
  "Privacy concerns",
  "Moved / no longer homeschooling",
  "Other",
];

function DeleteAccountDialog({ open, onOpenChange, deleting, onConfirm }) {
  const [reason, setReason] = useState("");
  const [other, setOther] = useState("");
  const finalReason = reason === "Other" ? (other.trim() ? `Other: ${other.trim()}` : "Other") : reason;
  return (
    <Dialog open={open} onOpenChange={(o) => !deleting && onOpenChange(o)}>
      <DialogContent className="rounded-3xl sm:max-w-md" data-testid="delete-account-dialog">
        <DialogHeader>
          <DialogTitle className="text-forest">Delete your account?</DialogTitle>
          <DialogDescription className="text-forest/70">
            This permanently deletes your account and all associated data. This cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2" data-testid="delete-reason-options">
          <p className="text-sm font-bold text-forest">Why are you leaving? <span className="font-normal text-forest/50">(optional)</span></p>
          {DELETE_REASONS.map((r) => (
            <label key={r} className="flex items-center gap-2 text-sm text-forest cursor-pointer">
              <input type="radio" name="delete-reason" value={r} checked={reason === r}
                onChange={() => setReason(r)} className="accent-terracotta"
                data-testid={`delete-reason-${r.toLowerCase().replace(/[^a-z]+/g, "-")}`} />
              {r}
            </label>
          ))}
          {reason === "Other" && (
            <Input value={other} onChange={(e) => setOther(e.target.value)} maxLength={200}
              placeholder="Tell us a little more..." className="rounded-xl" data-testid="delete-reason-other-input" />
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" className="rounded-full" onClick={() => onOpenChange(false)} disabled={deleting}
            data-testid="delete-account-cancel-btn">Cancel</Button>
          <Button className="rounded-full bg-terracotta-dark hover:bg-terracotta text-white" disabled={deleting}
            onClick={() => onConfirm(finalReason)} data-testid="delete-account-confirm-btn">
            {deleting ? "Deleting..." : "Delete my account"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MarketplaceBusinessBlock() {
  const [profile, setProfile] = useState({
    business_name: "", tagline: "", description: "",
    location: "", phone: "", contact_email: "", website: "",
    business_logo: "",
    product_categories: [],
    product_slots: [],
    social_link_1: "", social_link_2: "", social_link_3: "",
  });
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);

  const load = () => {
    api.get("/role-profile")
      .then((r) => {
        const p = r.data.profile || {};
        setProfile((prev) => ({
          ...prev,
          ...p,
          product_categories: Array.isArray(p.product_categories) ? p.product_categories : [],
          product_slots: Array.isArray(p.product_slots) ? p.product_slots : [],
        }));
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  };

  useEffect(() => { load(); }, []);

  const uploadLogo = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) return toast.error("Image is too large (max 10MB)");
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/marketplace-user/upload?kind=logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setProfile((p) => ({ ...p, business_logo: data.path }));
      toast.success("Logo uploaded");
    } catch (e) {
      toast.error(errMsg(e, "Upload failed"));
    } finally {
      setUploadingLogo(false);
    }
  };

  const toggleCategory = (opt) => {
    setProfile((p) => {
      const cur = p.product_categories || [];
      const next = cur.includes(opt) ? cur.filter((x) => x !== opt) : [...cur, opt];
      return { ...p, product_categories: next };
    });
  };

  const save = async () => {
    if (!profile.business_name.trim()) return toast.error("Shop or seller name is required");
    setSaving(true);
    try {
      await api.put("/role-profile", {
        business_name: profile.business_name,
        tagline: profile.tagline,
        description: profile.description,
        location: profile.location,
        phone: profile.phone,
        contact_email: profile.contact_email,
        website: profile.website,
        business_logo: profile.business_logo,
        product_categories: profile.product_categories,
        product_slots: profile.product_slots,
        social_link_1: profile.social_link_1,
        social_link_2: profile.social_link_2,
        social_link_3: profile.social_link_3,
      });
      toast.success("Business details updated");
    } catch (e) {
      toast.error(errMsg(e, "Could not save business details"));
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return null;
  const selectedCats = profile.product_categories || [];

  return (
    <div className="rounded-2xl border border-sage/20 bg-softstone/40 p-4 sm:p-5 space-y-4" data-testid="mk-business-details-section">
      <div className="flex items-center gap-2">
        <ShoppingBag className="w-4 h-4 text-sage-dark" />
        <h3 className="font-heading font-bold text-forest text-sm uppercase tracking-wide">Business details</h3>
      </div>

      <div>
        <Label>Business Logo</Label>
        <div className="mt-2 flex items-center gap-4">
          {profile.business_logo ? (
            <div className="relative w-24 h-24 rounded-2xl overflow-hidden border border-sage/30 shrink-0">
              <img src={fileUrl(profile.business_logo)} alt="Business logo" className="w-full h-full object-cover" data-testid="mk-biz-logo-preview" />
              <button type="button" onClick={() => setProfile((p) => ({ ...p, business_logo: "" }))}
                className="absolute top-1 right-1 bg-white/90 hover:bg-white p-1 rounded-full text-terracotta-dark"
                data-testid="mk-biz-logo-remove" aria-label="Remove logo">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : (
            <div className="w-24 h-24 rounded-2xl bg-white/60 border border-dashed border-sage/40 flex items-center justify-center text-forest/40 shrink-0">
              <Upload className="w-6 h-6" />
            </div>
          )}
          <div>
            <input ref={logoInputRef} type="file" accept="image/*" className="hidden"
              onChange={(e) => uploadLogo(e.target.files?.[0])} data-testid="mk-biz-logo-input" />
            <Button type="button" variant="outline" size="sm" className="rounded-full border-sage/40 text-forest"
              onClick={() => logoInputRef.current?.click()} disabled={uploadingLogo} data-testid="mk-biz-logo-btn">
              <Upload className="w-4 h-4 mr-2" />
              {uploadingLogo ? "Uploading..." : (profile.business_logo ? "Replace logo" : "Upload logo")}
            </Button>
          </div>
        </div>
      </div>

      <div>
        <Label>Shop / seller name*</Label>
        <Input value={profile.business_name} onChange={(e) => setProfile({ ...profile, business_name: e.target.value })}
          className="mt-1 rounded-xl bg-white border-sage/30" data-testid="mk-biz-name" />
      </div>

      <div>
        <Label>Tagline</Label>
        <Input value={profile.tagline} onChange={(e) => setProfile({ ...profile, tagline: e.target.value })}
          placeholder="e.g. Handmade wooden toys" className="mt-1 rounded-xl bg-white border-sage/30" data-testid="mk-biz-tagline" />
      </div>

      <div>
        <Label>About your shop</Label>
        <Textarea value={profile.description} onChange={(e) => setProfile({ ...profile, description: e.target.value })}
          rows={4} placeholder="What you sell, materials, delivery / collection info"
          className="mt-1 rounded-xl bg-white border-sage/30" data-testid="mk-biz-description" />
      </div>

      <div>
        <Label>Product Categories</Label>
        <Popover>
          <PopoverTrigger asChild>
            <button type="button"
              className="mt-1 w-full min-h-[42px] flex items-center justify-between gap-2 rounded-xl bg-white border border-sage/30 px-3 py-2 text-left hover:bg-softstone/40 transition-colors"
              data-testid="mk-biz-categories-trigger">
              <div className="flex flex-wrap gap-1.5 flex-1">
                {selectedCats.length === 0 ? (
                  <span className="text-sm text-forest/50">Select one or more product categories</span>
                ) : (
                  <span className="text-sm font-semibold text-forest" data-testid="mk-biz-categories-count">
                    {selectedCats.length} selected
                  </span>
                )}
              </div>
              <ChevronDown className="w-4 h-4 text-forest/50 shrink-0" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-[min(26rem,calc(100vw-2rem))] rounded-2xl bg-cream border-sage/20 p-2 shadow-lg" data-testid="mk-biz-categories-panel">
            <div className="flex items-center justify-between px-3 py-1">
              <p className="text-[10px] font-bold uppercase tracking-wide text-forest/50">Pick every category that applies</p>
              <button type="button"
                onClick={() => setProfile((p) => ({ ...p, product_categories: [] }))}
                disabled={selectedCats.length === 0}
                className="text-[11px] font-bold text-terracotta-dark hover:underline disabled:opacity-40 disabled:no-underline disabled:cursor-not-allowed"
                data-testid="mk-biz-categories-clear">
                Clear Selection
              </button>
            </div>
            <div className="max-h-72 overflow-y-auto">
              {PRODUCT_CATEGORY_OPTIONS.map((opt) => {
                const on = selectedCats.includes(opt);
                return (
                  <button key={opt} type="button" onClick={() => toggleCategory(opt)}
                    className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-xl text-sm text-left transition-colors ${on ? "bg-sage-light text-sage-dark font-semibold" : "text-forest/80 hover:bg-softstone/70"}`}
                    data-testid={`mk-biz-category-option-${opt}`}>
                    <span>{opt}</span>
                    {on && <Check className="w-4 h-4 text-sage-dark shrink-0" />}
                  </button>
                );
              })}
            </div>
          </PopoverContent>
        </Popover>
        {selectedCats.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5" data-testid="mk-biz-categories-chips">
            {selectedCats.map((c) => (
              <span key={c} className="inline-flex items-center gap-1 text-xs font-semibold text-sage-dark bg-sage-light/70 border border-sage/30 rounded-full pl-2.5 pr-1 py-0.5">
                {c}
                <button type="button" onClick={() => toggleCategory(c)} className="rounded-full hover:bg-white/60 p-0.5" aria-label={`Remove ${c}`} data-testid={`mk-biz-category-chip-remove-${c}`}>
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label>Location / area</Label>
          <Input value={profile.location} onChange={(e) => setProfile({ ...profile, location: e.target.value })}
            className="mt-1 rounded-xl bg-white border-sage/30" data-testid="mk-biz-location" />
        </div>
        <div>
          <Label>Contact phone</Label>
          <Input value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
            className="mt-1 rounded-xl bg-white border-sage/30" data-testid="mk-biz-phone" />
        </div>
        <div>
          <Label>Contact email</Label>
          <Input value={profile.contact_email} onChange={(e) => setProfile({ ...profile, contact_email: e.target.value })}
            className="mt-1 rounded-xl bg-white border-sage/30" data-testid="mk-biz-contact-email" />
        </div>
        <div>
          <Label>Website</Label>
          <Input value={profile.website} onChange={(e) => setProfile({ ...profile, website: e.target.value })}
            className="mt-1 rounded-xl bg-white border-sage/30" data-testid="mk-biz-website" />
        </div>
      </div>

      <div className="grid gap-3">
        <div>
          <Label>Social Media Link 1</Label>
          <Input value={profile.social_link_1} onChange={(e) => setProfile({ ...profile, social_link_1: e.target.value })}
            placeholder="https://" className="mt-1 rounded-xl bg-white border-sage/30" data-testid="mk-biz-social-1" />
        </div>
        <div>
          <Label>Social Media Link 2</Label>
          <Input value={profile.social_link_2} onChange={(e) => setProfile({ ...profile, social_link_2: e.target.value })}
            placeholder="https://" className="mt-1 rounded-xl bg-white border-sage/30" data-testid="mk-biz-social-2" />
        </div>
        <div>
          <Label>Social Media Link 3</Label>
          <Input value={profile.social_link_3} onChange={(e) => setProfile({ ...profile, social_link_3: e.target.value })}
            placeholder="https://" className="mt-1 rounded-xl bg-white border-sage/30" data-testid="mk-biz-social-3" />
        </div>
      </div>

      <Button onClick={save} disabled={saving}
        className="rounded-full bg-sage hover:bg-sage-dark text-white" data-testid="mk-biz-save-btn">
        {saving ? "Saving..." : "Update Business Details"}
      </Button>
    </div>
  );
}

